import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * PanicDivergence — Short-Covering / Long-Unwinding Trend Follower
 *
 * How it works:
 * - Option buyers make money when Option Sellers panic.
 * - This monitors `totalCallCount` and `totalPutCount`.
 * - If `totalCallCount` goes highly negative (Call Writers fleeing), it signals a Short-Covering Rally (Buy CE).
 * - If `totalPutCount` goes highly negative (Put Writers fleeing), it signals a Long-Unwinding Crash (Buy PE).
 */
export const evaluatePanicDivergence: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    // Detect Panic via Trend Analysis Data
    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    
    // Require history
    if (fullTrendData.length < 1) return null;

    const latestRow = fullTrendData[0];
    const callCount = latestRow?.totalCallCount ?? 0;
    const putCount = latestRow?.totalPutCount ?? 0;

    const panicThreshold = config.aggressiveness || 10; // E.g., a net count of -10 is panic

    let status: 'BULL' | 'BEAR' | 'Sideways' = 'Sideways';
    let remarks = '';

    // If Call writers are panicking (negative count) while Put writers aren't panicking as much
    if (callCount <= -panicThreshold && callCount < putCount) {
        status = 'BULL';
        remarks = `Call Writer Panic (Short Covering). CE Count: ${callCount}`;
    } 
    // If Put writers are panicking
    else if (putCount <= -panicThreshold && putCount < callCount) {
        status = 'BEAR';
        remarks = `Put Writer Panic (Long Unwinding). PE Count: ${putCount}`;
    }

    if (status === 'Sideways') return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    // We buy ATM or slightly OTM. Let's just buy ATM for liquidity and delta.
    const atmStrike = Math.round(spot / gap) * gap;
    const optionType = status === 'BULL' ? 'CE' : 'PE';

    let entryPrice = 0;
    const atmRow = liveFlatOpts.find((r: any) => r.strike === atmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status,
        strike: atmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: remarks
    };
};
